import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Router } from '@angular/router';
import moment from 'moment';
import { instanceNameValidator } from 'src/app/directives/instance-name.directives';
import { instanceEnvironmentValidator } from 'src/app/directives/instance-name.directives';

import { Constants } from 'src/app/models/constants';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { Deployment } from 'src/app/models/deploy/deployment';
import { DeploymentClass } from 'src/app/models/instance-class';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { Solution } from 'src/app/models/service/solution';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { PathToLiveEnvironment } from 'src/app/models/blueprints/path-to-live-environment';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {translate} from '@jsverse/transloco';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InstancesService } from 'src/app/services/api/instances.service';
import { Subject, Observable } from 'rxjs';
import { TenantInstanceService } from 'src/app/main/blueprint-summary/tenant-instance/tenant-instance.service';
import { InstanceType } from 'src/app/models/instance/instance-type';

@Component({
    selector: 'app-deploy-instance-form',
    templateUrl: './deploy-instance-form.component.html',
    styleUrls: ['./deploy-instance-form.component.scss'],
    standalone: false
})
export class DeployInstanceFormComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() deployment: Deployment;
  @Input() service: Solution;
  @Input() tenantDetails: any;
  @Input() versions: any;
  @Input() buildNumber: any;
  @Input() blueprint: any;
  @Input() allInstances: any;
  @Input() name: string;
  @Input() releaseVersion: string;
  @Input() requestOrigination: string;
  @Input() editMode: boolean;
  @Input() id: string;
  @Input() instance: any;
  @Input() title: boolean;
  @Input() subtitle: string;
  @Input() nonPtlFlow: boolean;
  @Input() pathToLiveFlow: boolean;
  @Output() changedBuildVersion = new EventEmitter<string>();
  @Output() submitDisable = new EventEmitter<boolean>();
  @Output() duplicateInstance = new EventEmitter<boolean>();

  DeploymentClass = DeploymentClass;
  createInstanceForm: FormGroup;

  Constants = Constants;

  DataZone = DataZone;
  nonPtlEnv = [InstanceType.OTHER, InstanceType.EAAS];

  weekDays: string[] = moment.weekdays();
  moment = moment;
  instanceName = false;
  devInstanceEnvironment: any;
  envt: any;
  finalBuild: any;
  submitDisableData: any;
  classDropdown: any;
  minDate: Date;
  maxDate: Date;
  isDateChanged = false;
  isLoading = false;
  contractEndDate: string;
  pathToLiveStatus: boolean;
  devFlow: boolean;
  enableDataZoneDropdown = false;
  disableWithDefault = false;
  PathToLiveEnvironment = PathToLiveEnvironment;

  constructor(private formBuilder: FormBuilder, public sidenavService: SidenavService, private instanceService: InstancesService, private dialog: MatDialog, private tenantInstanceService: TenantInstanceService) {

  }

  ngOnInit(): void {
    this.envt = window.location.href.split('.')[0].split('/')[2];
    this.createForm();

    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() + 1);
    this.minDate.setHours(0, 0, 0, 0);

    if (this.tenantDetails.tenantId) {
      const contractEndDate = new Date(this.tenantDetails.contractEndDate);
      contractEndDate.setHours(0, 0, 0, 0);
      this.maxDate = contractEndDate;
    }

    this.editMode = this.editMode || false;
    if (this.instance?.contractEndDate) {
      this.createInstanceForm.patchValue({
        contractEndDate: this.instance?.contractEndDate
      });
      const contractDate = new Date(this.instance?.contractEndDate);
      this.minDate = new Date(contractDate.setDate(contractDate.getDate() + 1));
    }
    this.createInstanceForm.get('contractEndDate')?.valueChanges.subscribe(value => {
      this.isDateChanged = value !== this.contractEndDate;
    });
    const dataZone = this.createInstanceForm.get('dataZone');
    const deploymentClass = this.createInstanceForm.get('deploymentClass')
    const targetEnv = this.deployment.targetEnvironment;
    if(this.pathToLiveStatus || this.nonPtlFlow) {
      if (targetEnv === PathToLiveEnvironment.PREPROD || targetEnv === PathToLiveEnvironment.LIVE) {
      this.enableDataZoneDropdown = false;
      this.disableWithDefault = true;
      this.createInstanceForm.get('dataZone')?.setValue(this.deployment.dataZone);
      this.createInstanceForm.get('deploymentClass')?.setValue(deploymentClass.value);
      this.createInstanceForm.get('dataZone')?.disable();
      this.createInstanceForm.get('deploymentClass')?.disable();
      dataZone?.clearValidators();
    } else {
      this.enableDataZoneDropdown = true;
      this.disableWithDefault = false;
      this.createInstanceForm.get('dataZone')?.enable();
      this.createInstanceForm.get('deploymentClass')?.enable();
      dataZone?.setValue('');
      dataZone?.setValidators([Validators.required]);
      deploymentClass?.setValue('');
      deploymentClass?.setValidators([Validators.required]);
    }
    }
  }
  shouldShowNote(): boolean {
    return this.tenantDetails.contractEndDate === this.instance?.contractEndDate;
  }

  editContractEndDate(instance: any): any   {
    event.stopPropagation();
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.EDIT_CONFIRMATION_TITLE'),
        message: translate('INSTANCE_DETAILS.EDIT_CONFIRMATION_SUBTITLE'),
        confirmButtonText: translate('DIALOG.CONFIRM')
      }
    }).afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        const editInstanceForm = this.createInstanceForm.getRawValue();
        editInstanceForm.contractEndDate = editInstanceForm.contractEndDate ? moment(editInstanceForm.contractEndDate).utcOffset("+05:30").format("YYYY-MM-DD") : editInstanceForm.contractEndDate;
        let data: any = {
          id : instance?.id,
          contractEndDate: editInstanceForm.contractEndDate
        }
        this.instanceService.sendingLogBasedOnSelection(data)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((instanceData: any) => {
            this.isLoading = true;
            this.tenantInstanceService.triggerReload();
            this.sidenavService.closeRightSidenav();
            this.isLoading = false;
          },(error)=>{
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
              this.dialog.open(ErrorDialogComponent, {
                    minHeight: '342px',
                    width: '630px',
                    panelClass: 'flex-dialog',
                    data: {
                      title: translate('ERROR_DIALOG.EDIT_DATE_FAILED'),
                      description: errorMessage
                    }
              });
          }
        );
      }
    });
  }

  ngAfterViewInit() {
this.createInstanceForm.get('deploymentClass')?.valueChanges.subscribe(value => {
    const dataZone = this.createInstanceForm.get('dataZone');
    const switchDeploymentClass = this.nonPtlFlow || this.pathToLiveStatus;

    if (switchDeploymentClass && value === DeploymentClass.D) {
      this.createInstanceForm.get('dataZone')?.setValue('nonpii');
      dataZone?.clearValidators();
      dataZone?.disable();
    } else if (switchDeploymentClass && (value === DeploymentClass.B || value === DeploymentClass.C)) {
      dataZone?.enable();
      dataZone?.setValue('');
      dataZone?.setValidators([Validators.required]);
    }

    dataZone?.updateValueAndValidity();
  });
  }

  getDeployment(): Deployment {
    if (!FormUtilService.validateForm(this.createInstanceForm)) {
      return;
    }
    return this.createInstanceForm.getRawValue();
  }

  onClick(): void {
    this.instanceName =true;
  }

  getBuildVersion(buildVersion?: any): void {
    this.changedBuildVersion.emit(buildVersion);
  }

  getVersion(value): void {
    const data = value.split(':')[1].trim();
    const selectedVersion = this.versions.find(version => version.id === data);
    this.populateReleaseVersion(selectedVersion);
  }

   private createForm(): void {

    const instanceClass = this.deployment.deploymentClass;
    const targetEnv = this.deployment.targetEnvironment;
    this.pathToLiveStatus = this.deployment.pathToLiveFlow;
    this.devFlow = this.deployment.devInstanceFlow;
    if(this.nonPtlFlow || (this.pathToLiveStatus && targetEnv !== PathToLiveEnvironment.PREPROD && targetEnv !== PathToLiveEnvironment.LIVE)){
      this.classDropdown = [DeploymentClass.B, DeploymentClass.C];
    } else{
    if (instanceClass === DeploymentClass.B || instanceClass === DeploymentClass.C) {
    // if ((instanceClass === DeploymentClass.B && this.deployment.targetEnvironment !== PathToLiveEnvironment.LIVE) || instanceClass === DeploymentClass.C) {
      this.classDropdown = [DeploymentClass.B, DeploymentClass.C];
    } else {
      this.classDropdown = [instanceClass];
    }
  }

    this.devInstanceEnvironment = this.deployment.targetEnvironment ? this.deployment.targetEnvironment :
      this.deployment.deploymentClass === DeploymentClass.D ? 'Development' : this.deployment.dataZone;
    this.deployment.blueprintVersionId = "";
    if (this.versions) {
      this.populateReleaseVersion(this.versions[0]);
    }
    const notAvailable = {version: 'Release Version not Available', releaseVersion: 'Release Version not Available'};
    if (this.finalBuild?.length === 0) {
      this.submitDisableData = true;
      this.submitDisable.emit(this.submitDisableData);
      this.finalBuild = [...this.finalBuild, notAvailable];
    }
    const formConfig: any = {
      name: [this.deployment.name, [Validators.required, Validators.maxLength(16),
        Validators.pattern(/^(?=.*[a-zA-Z])([a-zA-Z0-9 ]+)$/), instanceNameValidator(this.allInstances)]],
      region: [this.tenantDetails.regionPair || this.tenantDetails.temenosRegion],
      deploymentClass: [this.nonPtlFlow || this.pathToLiveStatus ? this.classDropdown[0] : this.deployment.deploymentClass],
      dataZone: [this.nonPtlFlow || this.pathToLiveStatus ? [this.deployment.dataZone, Validators.required] : this.deployment.dataZone],
      blueprintId: [this.deployment.blueprintId],
      targetEnvironment: [this.nonPtlFlow ? this.nonPtlEnv[0] : this.deployment.targetEnvironment ],
      blueprintVersionId: [this.deployment.blueprintVersionId, Validators.required],
      buildNumber: [this.finalBuild?.version || '', Validators.required],
      requestOrigination: [this.deployment.requestOrigination || '', Validators.required],
      contractEndDate: [this.deployment.contractEndDate, Validators.required]
    };

    // if (this.deployment?.dataZone === DataZone.LIVE) {
    //   formConfig.dayOfTheWeek = [undefined, Validators.required];
    //   formConfig.startingHour = [undefined, Validators.required];
    //   formConfig.endingHour = [undefined, Validators.required];
    // }

    this.createInstanceForm = this.formBuilder.group(formConfig);
  }

isDataZoneDropdownDisabled(): boolean {
    if (this.nonPtlFlow) return false;
  return this.devFlow || (
    this.pathToLiveStatus &&
    (this.deployment?.targetEnvironment === PathToLiveEnvironment.PREPROD || this.deployment?.targetEnvironment === PathToLiveEnvironment.LIVE)
  );
}

isDataZoneDisabledForClassD(): boolean {
  const deploymentClass = this.createInstanceForm?.get('deploymentClass')?.value;
  const isPathFlow = this.nonPtlFlow || this.pathToLiveStatus;
  return isPathFlow && deploymentClass === DeploymentClass.D;
}

  verifyDuplicate(): void {
    this.duplicateInstance.emit(this.createInstanceForm.controls.name.valid);
  }

  getEnvironmentValue(): string {
    // In edit mode, use instance object; in create mode, use deployment object
    const sourceObj = this.editMode ? this.instance : this.deployment;

    const targetEnv = sourceObj?.targetEnvironment || this.deployment?.targetEnvironment || '';
    const dataZone = sourceObj?.dataZone;
    const deploymentClass = sourceObj?.deploymentClass || this.deployment?.deploymentClass;

    // Determine the environment value based on deployment properties
    let envValue = targetEnv || (deploymentClass === DeploymentClass.D ? 'Development' : dataZone);

    // Apply title case if needed
    if (dataZone === DataZone.DEVELOPMENT && envValue) {
      return envValue.charAt(0).toUpperCase() + envValue.slice(1).toLowerCase();
    }

    return envValue || '';
  }

  getDataZoneValue(): string {
    // Use instance dataZone if available (edit mode), otherwise use deployment subscription
    if (this.instance?.dataZone) {
      return this.instance.dataZone.charAt(0).toUpperCase() + this.instance.dataZone.slice(1).toLowerCase();
    }
    // Fallback to deployment dataZone in create mode
    if (this.deployment?.dataZone) {
      return this.deployment.dataZone.charAt(0).toUpperCase() + this.deployment.dataZone.slice(1).toLowerCase();
    }
    return '';
  }

  private populateReleaseVersion(value): void {
    const initialBuildNumbers = this.buildNumber;
    let finalBuildValue: any = [];
    initialBuildNumbers.forEach(build => {
      if(build?.capabilities?.length > 0) {
        if(value?.capabilities?.length <= build?.capabilities?.length) {
          let add = true;
          value.capabilities.forEach(capability => {
            if(build.capabilities.find(buildCapability => buildCapability.name === capability.name)) {
              return;
            } else {
              add = false;
            }
          });
          if (add) {
            finalBuildValue = [...finalBuildValue, build];
          }
        }
      } else {
      finalBuildValue = [...finalBuildValue, build];
      }
    });
    this.finalBuild = finalBuildValue;
  }

}
